import { useEffect, useState } from 'react';

/**
 * Live-subscribes to a CSS media query and returns whether it currently
 * matches, re-rendering on viewport changes.
 *
 * Reach for this when a responsive decision CANNOT be expressed with Tailwind's
 * `hidden` / `min-[…]:block` classes — specifically when choosing between a
 * component that portals out of the DOM tree (e.g. the design-system
 * {@link Modal}, which `createPortal`s to `document.body`) and an inline one.
 * A `display:none` wrapper can't hide a portaled child, so the switch has to be
 * a real mount/unmount in JS, not CSS visibility.
 *
 * SSR-safe: returns `false` when `window` is unavailable, then syncs in the
 * effect once mounted.
 *
 *   const isDesktop = useMediaQuery('(min-width: 900px)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mql = window.matchMedia(query);
    const sync = () => setMatches(mql.matches);

    // The query (or the viewport) may have changed between the initial render
    // and this effect running — sync once before subscribing.
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, [query]);

  return matches;
}
